import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { getBulkBatches, getBatchRequests } from '../api';
import { getTmDisplayName, resolveTmId } from '../utils/languages';

function getWordCount(r) {
  if (r.source_word_count != null) return Number(r.source_word_count);
  const src = r.source || r.English || '';
  return src ? src.split(/\s+/).filter(Boolean).length : 0;
}

function formatDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return '-'; }
}

function Reports({ availableTMs = [] }) {
  const [batches, setBatches] = useState([]);
  const [batchRows, setBatchRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedTm, setSelectedTm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const allBatches = await getBulkBatches();
      setBatches(allBatches);
      const rowMap = {};
      for (const b of allBatches) {
        try {
          const rows = await getBatchRequests(b.batch_id);
          rowMap[b.batch_id] = rows;
        } catch { /* skip */ }
      }
      setBatchRows(rowMap);
    } catch (err) {
      console.error('Failed to fetch report data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Build report rows: one per file (batch)
  const reportRows = useMemo(() => {
    return batches.map((b) => {
      const rows = batchRows[b.batch_id] || [];
      const tmId = b.tm_id || (rows.length > 0 ? resolveTmId(rows[0], availableTMs) : '');
      const totalWords = rows.reduce((sum, r) => sum + getWordCount(r), 0);
      return {
        fileName: b.file_name || `batch-${b.batch_id?.substring(0, 8)}`,
        tmId,
        tmName: getTmDisplayName(tmId, availableTMs) || tmId,
        submittedDate: b.created_at || '',
        submittedBy: b.submitted_by || '',
        translationCount: rows.length || b.total_count || 0,
        totalWordCount: totalWords,
        batchId: b.batch_id,
      };
    });
  }, [batches, batchRows, availableTMs]);

  // Filters
  const filteredRows = useMemo(() => {
    let rows = reportRows;
    if (selectedTm) {
      rows = rows.filter((r) => r.tmId === selectedTm);
    }
    if (startDate) {
      rows = rows.filter((r) => r.submittedDate >= startDate);
    }
    if (endDate) {
      const endDay = endDate + 'T23:59:59';
      rows = rows.filter((r) => r.submittedDate <= endDay);
    }
    return rows;
  }, [reportRows, selectedTm, startDate, endDate]);

  // Sort
  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'file': cmp = a.fileName.localeCompare(b.fileName); break;
        case 'tm': cmp = a.tmName.localeCompare(b.tmName); break;
        case 'date': cmp = a.submittedDate.localeCompare(b.submittedDate); break;
        case 'user': cmp = a.submittedBy.localeCompare(b.submittedBy); break;
        case 'count': cmp = a.translationCount - b.translationCount; break;
        case 'words': cmp = a.totalWordCount - b.totalWordCount; break;
        default: break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredRows, sortKey, sortDir]);

  // TM options from report data
  const tmOptions = useMemo(() => {
    const seen = new Map();
    for (const r of reportRows) {
      if (r.tmId && !seen.has(r.tmId)) {
        seen.set(r.tmId, r.tmName);
      }
    }
    return Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ id, name }));
  }, [reportRows]);

  const handleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); }
    else if (sortDir === 'asc') { setSortDir('desc'); }
    else { setSortKey('date'); setSortDir('desc'); }
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return <span className="sort-indicator sort-indicator--idle">↕</span>;
    return <span className="sort-indicator sort-indicator--active">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  // Totals
  const totalTranslations = sortedRows.reduce((s, r) => s + r.translationCount, 0);
  const totalWords = sortedRows.reduce((s, r) => s + r.totalWordCount, 0);

  // Export to CSV (lightweight, no external dependency)
  const exportCsv = () => {
    const header = ['File Name', 'TM', 'Submitted Date', 'Submitted By', 'Translation Count', 'Total Word Count'];
    const csvRows = [header.join(',')];
    for (const r of sortedRows) {
      csvRows.push([
        `"${r.fileName}"`,
        `"${r.tmName}"`,
        `"${formatDate(r.submittedDate)}"`,
        `"${r.submittedBy}"`,
        r.translationCount,
        r.totalWordCount,
      ].join(','));
    }
    csvRows.push('');
    csvRows.push(`"Total","","","",${totalTranslations},${totalWords}`);
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `word-count-by-file-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-header-content">
          <div className="step-number">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </div>
          <div className="card-header-text">
            <h2>Reports</h2>
            <p>Word count by file report</p>
          </div>
        </div>
      </div>
      <div className="card-body">
        {/* Filters */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>TM</label>
            <select
              value={selectedTm}
              onChange={(e) => setSelectedTm(e.target.value)}
              style={{ fontSize: '0.85rem', padding: '0.3rem 0.5rem', borderRadius: '6px', border: '1px solid #ccc' }}
            >
              <option value="">All Translation Memories</option>
              {tmOptions.map((tm) => (
                <option key={tm.id} value={tm.id}>{tm.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ fontSize: '0.85rem', padding: '0.3rem 0.5rem', borderRadius: '6px', border: '1px solid #ccc' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ fontSize: '0.85rem', padding: '0.3rem 0.5rem', borderRadius: '6px', border: '1px solid #ccc' }}
            />
          </div>
          <button
            className="btn btn-secondary btn-small"
            onClick={fetchData}
            disabled={loading}
          >
            {loading ? 'Loading...' : '↻ Refresh'}
          </button>
          <button
            className="btn btn-primary btn-small"
            onClick={exportCsv}
            disabled={sortedRows.length === 0}
          >
            ↓ Export CSV
          </button>
        </div>

        {/* Summary */}
        {sortedRows.length > 0 && (
          <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: '#555' }}>
            {sortedRows.length} files · {totalTranslations.toLocaleString()} translations · {totalWords.toLocaleString()} words
          </div>
        )}

        {loading && sortedRows.length === 0 ? (
          <div className="empty-state">
            <span className="spinner"></span>
            <p>Loading report data...</p>
          </div>
        ) : sortedRows.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📊</div>
            <p>No file data available for the selected filters.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table data-table--compact">
              <thead>
                <tr>
                  <th>
                    <button type="button" className="th-sort" onClick={() => handleSort('file')}>
                      File Name {sortIndicator('file')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort" onClick={() => handleSort('tm')}>
                      TM {sortIndicator('tm')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort" onClick={() => handleSort('date')}>
                      Submitted {sortIndicator('date')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort" onClick={() => handleSort('user')}>
                      By {sortIndicator('user')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort" onClick={() => handleSort('count')}>
                      Translations {sortIndicator('count')}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort" onClick={() => handleSort('words')}>
                      Word Count {sortIndicator('words')}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr key={row.batchId}>
                    <td className="text-wrap" style={{ maxWidth: '250px' }}>{row.fileName}</td>
                    <td className="text-wrap" style={{ maxWidth: '180px', fontSize: '0.85rem' }}>{row.tmName}</td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{formatDate(row.submittedDate)}</td>
                    <td className="text-wrap" style={{ maxWidth: '180px', fontSize: '0.85rem' }}>{row.submittedBy}</td>
                    <td style={{ textAlign: 'right', fontSize: '0.85rem' }}>{row.translationCount.toLocaleString()}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, fontSize: '0.85rem' }}>{row.totalWordCount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid #cbd5e0' }}>
                  <td colSpan={4}>Total</td>
                  <td style={{ textAlign: 'right' }}>{totalTranslations.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{totalWords.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Reports;
